import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { AppDialogModal, NotificationType } from '../components/AppDialogModal';
import { formatUserFriendlyError } from './errorUtils';

export interface DialogOptions {
  type?: NotificationType;
  title?: string;
  message: string;
  confirmText?: string;
  onConfirm?: () => void;
}

interface DialogContextType {
  showDialog: (options: DialogOptions) => void;
  showError: (message: string | unknown, title?: string) => void;
  showSuccess: (message: string, title?: string) => void;
  showInfo: (message: string, title?: string) => void;
  showWarning: (message: string, title?: string) => void;
  closeDialog: () => void;
}

const DialogContext = createContext<DialogContextType | undefined>(undefined);

// Event listener / global trigger for non-React code or window.alert bridge
type DialogListener = (opts: DialogOptions | null) => void;
let globalDialogListener: DialogListener | null = null;

export const showGlobalDialog = (opts: DialogOptions) => {
  if (globalDialogListener) {
    globalDialogListener(opts);
  } else {
    console.warn('Dialog requested before DialogProvider mounted:', opts);
  }
};

export const showGlobalError = (err: unknown, title: string = 'Operation Error') => {
  const message = formatUserFriendlyError(err);
  showGlobalDialog({
    type: 'error',
    title,
    message,
  });
};

export const showGlobalSuccess = (message: string, title: string = 'Success') => {
  showGlobalDialog({
    type: 'success',
    title,
    message,
  });
};

export const DialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [dialog, setDialog] = useState<DialogOptions | null>(null);

  const showDialog = useCallback((options: DialogOptions) => {
    setDialog(options);
  }, []);

  const closeDialog = useCallback(() => {
    if (dialog?.onConfirm) {
      dialog.onConfirm();
    }
    setDialog(null);
  }, [dialog]);

  const showError = useCallback((err: unknown, title = 'Operation Error') => {
    showDialog({
      type: 'error',
      title,
      message: formatUserFriendlyError(err),
    });
  }, [showDialog]);

  const showSuccess = useCallback((message: string, title = 'Success') => {
    showDialog({
      type: 'success',
      title,
      message,
    });
  }, [showDialog]);

  const showInfo = useCallback((message: string, title = 'Information') => {
    showDialog({
      type: 'info',
      title,
      message,
    });
  }, [showDialog]);

  const showWarning = useCallback((message: string, title = 'Warning') => {
    showDialog({
      type: 'warning',
      title,
      message,
    });
  }, [showDialog]);

  useEffect(() => {
    globalDialogListener = (opts) => {
      setDialog(opts);
    };

    // Intercept window.alert so ANY alert() call in any component or library
    // renders the site's own styled modal pop-up instead of browser alert dialog.
    const originalAlert = window.alert;
    window.alert = (message?: any) => {
      const msgStr = typeof message === 'string' ? message : String(message ?? '');
      const isError = /error|fail|invalid|cannot|denied|hata|basarisiz/i.test(msgStr);
      const isSuccess = /success|successful|saved|updated|created|locked|başarı/i.test(msgStr);
      const isWarning = /warn|mandatory|required|please|uyarı/i.test(msgStr);

      const type: NotificationType = isError ? 'error' : isSuccess ? 'success' : isWarning ? 'warning' : 'info';
      const title = isError ? 'Operation Error' : isSuccess ? 'Success' : isWarning ? 'Notice' : 'Information';

      setDialog({
        type,
        title,
        message: isError ? formatUserFriendlyError(msgStr) : msgStr,
      });
    };

    return () => {
      globalDialogListener = null;
      window.alert = originalAlert;
    };
  }, []);

  return (
    <DialogContext.Provider value={{ showDialog, showError, showSuccess, showInfo, showWarning, closeDialog }}>
      {children}
      {dialog && (
        <AppDialogModal
          isOpen={true}
          type={dialog.type || 'info'}
          title={dialog.title || 'Notice'}
          message={dialog.message}
          confirmText={dialog.confirmText || 'OK'}
          onClose={closeDialog}
        />
      )}
    </DialogContext.Provider>
  );
};

export const useDialog = (): DialogContextType => {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    return {
      showDialog: showGlobalDialog,
      showError: showGlobalError,
      showSuccess: showGlobalSuccess,
      showInfo: (msg, title = 'Information') => showGlobalDialog({ type: 'info', title, message: msg }),
      showWarning: (msg, title = 'Warning') => showGlobalDialog({ type: 'warning', title, message: msg }),
      closeDialog: () => {
        if (globalDialogListener) globalDialogListener(null);
      },
    };
  }
  return ctx;
};
